'use client'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { ContentRelationshipField } from '@prismicio/types'
import { BlogpostSlicedDocument } from '@root/prismicio-types'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'

import { BlogAuthor, getAuthor, getValidatedCustomLabel } from '@/app/(main)/resource-hub/utils'
import BlogCard from '@/components/BlogCard'
import ResourceHub from '@/sections/ResourceHub'

export const ResourceHubLayout: FC<{
    featuredPost?: BlogpostSlicedDocument
    featuredFreeGuide?: {
        href: string
        title: string
        imageSrc: string
        formattedDate: string
        author?: { id: string }
    }
    selectedPosts?: BlogpostSlicedDocument[]
    freeGuides?: {
        author?: { id: string }
        label?: string
        href: string
        title: string
        imageSrc: string
        formattedDate: string
    }[]
    posts: BlogpostSlicedDocument[]
    authors: BlogAuthor[]
}> = ({ featuredPost, featuredFreeGuide, selectedPosts, posts, freeGuides, authors }) => {
    const [page, setPage] = useState<number>(1)

    useEffect(() => {
        const lastViewedPage = sessionStorage.getItem('lastViewedPage')
        if (lastViewedPage) {
            setPage(Number(lastViewedPage))
        }
    }, [])

    const saveLastViewedPage = () => {
        sessionStorage.setItem('lastViewedPage', page.toString())
    }

    return (
        <Stack spacing={{ xs: 4, sm: 6, md: 9 }}>
            {featuredFreeGuide && (
                <BlogCard
                    size={'L'}
                    key={`featured-free-guide-${featuredFreeGuide.title}`}
                    sx={{ width: '100%' }}
                    label={'Free guide'}
                    href={featuredFreeGuide.href}
                    imageSrc={featuredFreeGuide.imageSrc}
                    title={featuredFreeGuide.title}
                    date={moment(featuredFreeGuide.formattedDate, moment.ISO_8601, true)}
                    author={
                        featuredFreeGuide.author
                            ? getAuthor(
                                  authors,
                                  featuredFreeGuide.author as ContentRelationshipField<'author'>,
                              )
                            : {
                                  name: 'Lune',
                                  avatar: 'https://images.prismic.io/luneco/b1a4704c-7a99-463a-ad4f-afac057fe919_Lune_icon.jpg?auto=compress,format',
                                  title: undefined,
                              }
                    }
                />
            )}
            {featuredPost && (
                <BlogCard
                    size={'L'}
                    key={`blog-card-${featuredPost.id}`}
                    label={'Insight'}
                    customLabel={getValidatedCustomLabel(featuredPost.data.custom_label)}
                    sx={{ width: '100%' }}
                    title={featuredPost.data.title[0]!.text}
                    imageSrc={
                        'image' in featuredPost.data.body[0]!.primary &&
                        featuredPost.data.body[0]!.primary.image.url!
                            ? featuredPost.data.body[0]!.primary.image.url
                            : featuredPost.data.main_image.url!
                    }
                    date={moment(featuredPost.data.date, moment.ISO_8601, true)}
                    lastPublishedDate={featuredPost.last_publication_date}
                    href={`/blog/${featuredPost.uid}`}
                    onClick={saveLastViewedPage}
                    author={getAuthor(authors, featuredPost.data.author)}
                />
            )}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 5, sm: 5, md: '72px 24px' },
                }}
            >
                {freeGuides?.map((freeGuide, index) => (
                    <BlogCard
                        size={'M'}
                        key={`selected-free-guide-${index}`}
                        sx={{
                            flexGrow: 1,
                            flexShrink: 1,
                            flexBasis: {
                                md: 'calc(50% - 12px)',
                                sm: 'calc(50% - 20px)',
                                xs: '100%',
                            },
                            maxWidth: {
                                md: 'calc(50% - 12px)',
                                sm: 'calc(50% - 20px)',
                                xs: '100%',
                            },
                        }}
                        label={'Free guide'}
                        {...(freeGuide.label
                            ? {
                                  customLabel: getValidatedCustomLabel(freeGuide.label),
                              }
                            : {})}
                        href={freeGuide.href}
                        imageSrc={freeGuide.imageSrc}
                        title={freeGuide.title}
                        date={moment(freeGuide.formattedDate, moment.ISO_8601, true)}
                        author={
                            freeGuide.author
                                ? getAuthor(
                                      authors,
                                      freeGuide.author as ContentRelationshipField<'author'>,
                                  )
                                : {
                                      name: 'Lune',
                                      avatar: 'https://images.prismic.io/luneco/b1a4704c-7a99-463a-ad4f-afac057fe919_Lune_icon.jpg?auto=compress,format',
                                      title: undefined,
                                  }
                        }
                    />
                ))}

                {selectedPosts?.map((selectedPost, index) => (
                    <BlogCard
                        size={'M'}
                        label={'Insight'}
                        customLabel={getValidatedCustomLabel(selectedPost.data.custom_label)}
                        sx={{
                            flexGrow: 1,
                            flexShrink: 1,
                            flexBasis: {
                                md: 'calc(50% - 12px)',
                                sm: 'calc(50% - 20px)',
                                xs: '100%',
                            },
                            maxWidth: {
                                md: 'calc(50% - 12px)',
                                sm: 'calc(50% - 20px)',
                                xs: '100%',
                            },
                        }}
                        key={`selected-post-${index}`}
                        title={selectedPost.data.title[0]!.text}
                        imageSrc={
                            'image' in selectedPost.data.body[0]!.primary &&
                            selectedPost.data.body[0]!.primary.image.url!
                                ? selectedPost.data.body[0]!.primary.image.url
                                : selectedPost.data.main_image.url!
                        }
                        date={moment(selectedPost.data.date, moment.ISO_8601, true)}
                        lastPublishedDate={selectedPost.last_publication_date}
                        href={`/blog/${selectedPost.uid}`}
                        onClick={saveLastViewedPage}
                        author={getAuthor(authors, selectedPost.data.author)}
                    />
                ))}
            </Box>
            <ResourceHub content={posts} authors={authors} />
        </Stack>
    )
}
